// ─── UI Store (Zustand) ───
// Global UI state: sidebar, theme, active view.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isSidebarUndocked } from '../lib/viewport';

// GHE #284 item 1 — the canonical 5-member tuple, exported so
// `features/filters/useTaskFilters.ts` can build the `viewMode` URL search
// param's zod enum from the SAME list `ActiveView` is derived from (a single
// source of truth — the URL schema and this store type can never drift).
export const ACTIVE_VIEW_OPTIONS = ['list', 'board', 'gantt', 'calendar', 'table'] as const;
export type ActiveView = (typeof ACTIVE_VIEW_OPTIONS)[number];
export type Theme = 'light' | 'dark' | 'system';

/**
 * GHE #1279 — cuánta transparencia quiere ver esta persona en los menús flotantes.
 *
 * `system` delega en `prefers-reduced-transparency`. Hace falta poder decirlo explícitamente porque
 * **Safari no implementa esa consulta** (bug de WebKit abierto desde 2017) y Firefox la trae
 * apagada por huella digital: macOS inventó el ajuste «Reducir transparencia» y su propio navegador
 * no lo expone. Quien lo necesita en un Mac con Safari no tendría forma de pedirlo.
 */
export type Transparency = 'system' | 'on' | 'off';

/** A recently-visited space/list (#185). Captures the `name` AT VISIT TIME so the Recent
 *  section renders with zero lookups (unlike server Favorites, which resolve names on read). */
export interface RecentEntity {
  type: 'SPACE' | 'LIST';
  id: string;
  name: string;
  /**
   * Where this entity lives, e.g. "Software Factory · Sprint 14" (#325). Captured at visit time
   * like `name`, because Recents renders with ZERO lookups — resolving it later would mean an
   * API call per row, which is exactly what this section is designed to avoid.
   *
   * Optional: entries persisted before this existed have no context, and rows fall back to the
   * bare name rather than showing a placeholder.
   */
  context?: string;
  /** Epoch ms of the most recent visit (used only for ordering; newest first). */
  at: number;
}

/**
 * Join location parts into a Recents subtitle, dropping blanks and consecutive repeats (#325).
 *
 * A space very often carries its workspace's name — the dev tenant alone has "Software Factory"
 * inside "Software Factory" and "Data" inside "Data" — which rendered as "Software Factory ·
 * Software Factory": pure noise, and it spent half the row saying one word twice in a sidebar
 * that only has ~120px for the whole subtitle.
 *
 * Returns undefined rather than an empty string so callers can leave `context` off entirely.
 */
export function recentContext(...parts: (string | undefined | null)[]): string | undefined {
  const joined = parts
    .filter((p): p is string => Boolean(p && p.trim()))
    .filter((p, i, arr) => i === 0 || p !== arr[i - 1])
    .join(' · ');
  return joined || undefined;
}

/** How many recent items to keep (client-only, capped so localStorage never grows unbounded). */
const RECENTS_CAP = 8;

interface UIState {
  // Sidebar
  sidebarOpen: boolean;

  // Active company (ADR 0016) — mirrors activeWorkspaceId's persisted-client-state
  // pattern. The EFFECTIVE company (what queries actually use) is derived by
  // features/companies/useActiveCompany.ts, which falls back to the user's first
  // accessible company when this persisted id isn't (or is no longer) one of theirs.
  activeCompanyId: string | null;

  // Active workspace
  activeWorkspaceId: string | null;

  // Active space (highlighted in sidebar tree)
  activeSpaceId: string | null;

  // Currently-OPEN list (GHE #319) — highlighted in the sidebar tree, Recents and Favorites.
  // Set by the list route on mount and cleared on unmount, so it mirrors "which list page is
  // open right now", never "last visited". The old row checks compared `params.listId`, which
  // silently died when the friendly `/app/l/{ref}` URL became canonical (its only param is
  // `listRef`). Not persisted, same as activeSpaceId.
  activeListId: string | null;

  // Folder CONTAINING the currently-open list, or null when the list sits directly in its
  // space (GHE #319). Lets FolderNode reveal itself on a deep link the same one-way manner
  // SpaceNode does — without this, the active list row of a folder-nested list stays hidden
  // behind a collapsed folder. Maintained by the list route alongside activeListId.
  activeListFolderId: string | null;

  // View
  activeView: ActiveView;

  // Theme
  theme: Theme;

  // GHE #1279 — preferencia de transparencia. Mismo rango que `theme` y, como él, vive también
  // en el servidor (`User.transparency`): sigue a la persona entre máquinas, que es el punto.
  transparency: Transparency;

  // Gantt view preferences — GHE #437 item 16 moved the Gantt's zoom + toggle state into the
  // /app/l/$listRef URL (features/gantt/useGanttViewParams.ts) so it's shareable. The two
  // fields below stay here ONLY as that hook's legacy-bookmark fallback + write-through mirror
  // for users who already had a preference persisted before that change (same reasoning
  // useTaskFilters.ts uses to keep localStorage as a `viewMode` fallback). `ganttShowBacklog`
  // used to live here too but had NO persisted value to preserve (see `persistedUIState`
  // below) and has been removed entirely — the URL is now its only state.
  // ADR 0029 / #382 — when false (default), people who have left do not appear as Gantt
  // lanes at all ("don't show them directly on the gantt that they previously appeared").
  // Toggling this on reveals them, each lane marked with a red tint + "Baja" chip so they
  // read as unmistakably different from an active person at a glance. Persisted (see
  // `partialize` below) so the choice survives a reload, same tier as `theme`.
  ganttShowDisabledUsers: boolean;

  // Workload Gantt label density (#423) — OFF means only wide bars and the bar covering today
  // carry a visible title; ON labels every bar (still subject to #421's free-room rule, or they
  // overlap exactly as reported). A dense lane cannot show readable text for every bar at any
  // zoom — 24px holds about two characters — so labelling everything produces a row of
  // unreadable stubs. Persisted, same tier as `theme` and `ganttShowDisabledUsers`: it is a
  // per-person reading preference, and having to re-enable it every visit is friction. Note
  // this is a VISUAL density control only — every bar keeps its `aria-label` and `title`
  // regardless, so nothing changes for a screen reader or a hover.
  ganttShowAllTaskNames: boolean;

  // Dashboard engine (ADR 0021, Phase 12 Batch 2) — last-viewed dashboard id.
  // Same "UI preference, not server state" tier as `sidebarCollapsed` (design
  // spec §1.1's resolution order, step 1): a client-persisted pointer only,
  // never treated as authoritative (a dashboard that's since been deleted or
  // made unreachable is silently skipped by the resolver in favor of step 2).
  lastViewedDashboardId: string | null;

  // Recently-visited spaces/lists (#185) — client-only, capped, newest-first. A "UI
  // preference, not server state" tier like the pointers above; a since-deleted entity's
  // stale link simply 404s if clicked (never authoritative).
  recentEntities: RecentEntity[];

  // A bottom-centre floating bar (today: the bulk action bar) is mounted (#558). Read by the
  // realtime status banner, which shares that anchor and otherwise paints ON TOP of the bar —
  // the banner offsets itself upward while this is true, so both stay fully visible. Transient
  // presence, NOT persisted: surviving a reload would leave the banner floating mid-page with
  // no bar under it.
  bottomBarPresent: boolean;

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setActiveView: (view: ActiveView) => void;
  setTheme: (theme: Theme) => void;
  setTransparency: (transparency: Transparency) => void;
  setActiveCompanyId: (id: string | null) => void;
  setActiveWorkspaceId: (id: string | null) => void;
  setActiveSpaceId: (id: string | null) => void;
  setActiveListId: (id: string | null) => void;
  setActiveListFolderId: (id: string | null) => void;
  setGanttShowDisabledUsers: (show: boolean) => void;
  setGanttShowAllTaskNames: (show: boolean) => void;
  setLastViewedDashboardId: (id: string | null) => void;
  /** Record a visit to a space/list (dedup by type+id, prepend, cap at RECENTS_CAP). */
  pushRecent: (entry: Omit<RecentEntity, 'at'>) => void;
  setBottomBarPresent: (present: boolean) => void;
}

/** Apply dark class to <html> based on theme preference */
function applyThemeClass(theme: Theme): void {
  if (typeof document === 'undefined') return;

  const html = document.documentElement;

  if (theme === 'dark') {
    html.classList.add('dark');
  } else if (theme === 'light') {
    html.classList.remove('dark');
  } else {
    // system
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }
}

/**
 * Marca en `<html>` la ELECCIÓN de la persona (GHE #1279).
 *
 * ⚠️ `system` no pone clase a propósito, y eso no es un olvido: sin clase manda
 * `@media (prefers-reduced-transparency: reduce)` directamente desde el CSS. Por eso —al revés que
 * el tema, que sí necesita JS porque el modo oscuro de Tailwind va por clase— aquí **no hace falta
 * escuchar cambios de la preferencia del sistema**: el navegador reevalúa la consulta él solo.
 */
function applyTransparencyClass(transparency: Transparency): void {
  if (typeof document === 'undefined') return;

  const html = document.documentElement;
  html.classList.toggle('transparency-on', transparency === 'on');
  html.classList.toggle('transparency-off', transparency === 'off');
}

/**
 * Exactly which slice of the store survives a reload.
 *
 * Extracted from the inline `partialize` and exported so it can be tested: `ui.store.test.ts`
 * mocks `zustand/middleware`'s `persist` away entirely (the store has to initialise
 * synchronously there), which means an inline `partialize` is never executed by any test —
 * forgetting to add a key, or adding one that should not outlive the session, would be invisible.
 *
 * The rule for what belongs here: a per-person PREFERENCE (`theme`, label density) or a
 * navigational pointer that is never treated as authoritative. `ganttShowBacklog` no longer
 * exists on `UIState` at all (GHE #437 item 16 moved Gantt view state to the URL, see
 * features/gantt/useGanttViewParams.ts) — it was deliberately never persisted here even before
 * that move, so there was nothing to migrate; `ganttShowDisabledUsers`/`ganttShowAllTaskNames`
 * stay below ONLY as that hook's legacy-bookmark fallback for a preference that already existed.
 */
export function persistedUIState(state: UIState): Partial<UIState> {
  return {
    theme: state.theme,
    // GHE #1279 — mismo nivel que `theme`: una preferencia personal, y volver a ponerla en cada
    // visita es fricción. Además se escribe en el servidor, así que esto es sólo el eco local.
    transparency: state.transparency,
    activeWorkspaceId: state.activeWorkspaceId,
    activeCompanyId: state.activeCompanyId,
    lastViewedDashboardId: state.lastViewedDashboardId,
    recentEntities: state.recentEntities,
    // ADR 0029 / #382 — persisted per user across sessions (explicit brief requirement).
    ganttShowDisabledUsers: state.ganttShowDisabledUsers,
    // #423 — a per-person reading preference, so it survives a reload like the two above.
    ganttShowAllTaskNames: state.ganttShowAllTaskNames,
  };
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // GHE #801 — open only where it DOCKS. Below lg it is an overlay drawer, and a
      // drawer that starts open covers the content it was meant to navigate to.
      sidebarOpen: !isSidebarUndocked(),
      activeCompanyId: null,
      activeWorkspaceId: null,
      activeSpaceId: null,
      activeListId: null,
      activeListFolderId: null,
      activeView: 'list',
      theme: 'system',
      transparency: 'system',
      ganttShowDisabledUsers: false,
      // #423 — OFF by default: the whole point is that a dense lane reads better when only the
      // bar that answers "what is this person on right now?" is labelled.
      ganttShowAllTaskNames: false,
      lastViewedDashboardId: null,
      recentEntities: [],
      bottomBarPresent: false,

      setBottomBarPresent: (present: boolean): void => {
        set({ bottomBarPresent: present });
      },

      toggleSidebar: (): void => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      setSidebarOpen: (open: boolean): void => {
        set({ sidebarOpen: open });
      },

      setActiveView: (view: ActiveView): void => {
        set({ activeView: view });
      },

      setTheme: (theme: Theme): void => {
        set({ theme });
        applyThemeClass(theme);
      },

      setTransparency: (transparency: Transparency): void => {
        set({ transparency });
        applyTransparencyClass(transparency);
      },

      setActiveCompanyId: (id: string | null): void => {
        set({ activeCompanyId: id });
      },

      setActiveWorkspaceId: (id: string | null): void => {
        set({ activeWorkspaceId: id });
      },

      setActiveSpaceId: (id: string | null): void => {
        set({ activeSpaceId: id });
      },

      setActiveListId: (id: string | null): void => {
        set({ activeListId: id });
      },

      setActiveListFolderId: (id: string | null): void => {
        set({ activeListFolderId: id });
      },

      setGanttShowDisabledUsers: (show: boolean): void => {
        set({ ganttShowDisabledUsers: show });
      },

      setGanttShowAllTaskNames: (show: boolean): void => {
        set({ ganttShowAllTaskNames: show });
      },

      setLastViewedDashboardId: (id: string | null): void => {
        set({ lastViewedDashboardId: id });
      },

      pushRecent: (entry: Omit<RecentEntity, 'at'>): void => {
        set((state) => {
          // Dedup by type+id (a re-visit moves it to the front and refreshes its name), then
          // prepend the fresh entry and cap the list so localStorage never grows unbounded.
          const withoutDup = state.recentEntities.filter(
            (r) => !(r.type === entry.type && r.id === entry.id),
          );
          return {
            recentEntities: [{ ...entry, at: Date.now() }, ...withoutDup].slice(0, RECENTS_CAP),
          };
        });
      },
    }),
    {
      name: 'a8planner-ui',
      partialize: persistedUIState,
      onRehydrateStorage: (): ((state: UIState | undefined) => void) => {
        return (state: UIState | undefined): void => {
          if (state) {
            applyThemeClass(state.theme);
            applyTransparencyClass(state.transparency);
          }
        };
      },
    },
  ),
);

// ─── Listen for system theme changes ───
if (typeof window !== 'undefined') {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const { theme } = useUIStore.getState();
    if (theme === 'system') {
      applyThemeClass('system');
    }
  });

  // Apply theme on initial load
  const initialTheme = useUIStore.getState().theme;
  applyThemeClass(initialTheme);
  applyTransparencyClass(useUIStore.getState().transparency);
}
